import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { blogApi } from '../../api/blog'
import type { BlogPostSummary } from '../../types/blog'
import BlogShell from './BlogShell'
import {
  applyLandingSeo,
  initLandingAnalytics,
  useLandingDarkThemeEffect,
} from './landingUtils'

function formatBlogDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export default function BlogListPage() {
  const [posts, setPosts] = useState<BlogPostSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    initLandingAnalytics()
    applyLandingSeo({
      page: 'blog',
      title: 'Новости KartinAI — блог разработчика',
      description: 'Обновления продукта, новые модели и советы по генерации изображений и видео в KartinAI.',
      path: '/blog',
    })
  }, [])

  useEffect(useLandingDarkThemeEffect(), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    blogApi.listPosts()
      .then((res) => {
        if (!cancelled) {
          setPosts(res.data.items)
          setError(null)
        }
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить новости')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  return (
    <BlogShell>
      <div className="blog-container">
        <header className="blog-list-header">
          <h1 className="blog-list-title">Блог разработчика</h1>
          <p className="blog-list-desc">
            Новости приложения KartinAI: релизы, модели, улучшения интерфейса.
          </p>
        </header>

        {loading ? (
          <p className="blog-empty">Загрузка…</p>
        ) : error ? (
          <p className="blog-empty">{error}</p>
        ) : posts.length === 0 ? (
          <p className="blog-empty">Пока нет опубликованных записей.</p>
        ) : (
          <div className="blog-card-list">
            {posts.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="blog-card">
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt={post.cover_image_alt || post.title}
                    className="blog-card-cover"
                    loading="lazy"
                  />
                ) : null}
                <div className="blog-card-body">
                  <div className="blog-card-meta">
                    <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
                    {post.tags.map((tag) => (
                      <span key={tag} className="blog-tag">{tag}</span>
                    ))}
                  </div>
                  <h2 className="blog-card-title">{post.title}</h2>
                  {post.excerpt ? (
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </BlogShell>
  )
}
